import React, { useState } from "react";
import { Modal, Button, Nav } from "react-bootstrap";
import LLMSettings from "./components/LLMSettings";
import InstructionsSettings from "./components/InstructionsSettings";
import SchemaSettings from "./components/SchemaSettings";
import CheckerManagerSettings from "./components/CheckerManagerSettings";
import { downloadFile } from "../../utils/fileUtils";
import { useHandleFileUpload } from "../../hooks/useHandleFileUpload";
import initialSettings from "../../constants/settings.json";
import { storageKeys } from "../../constants/storageKeys";

const SettingsModal = ({ show, handleClose }) => {
  const [activeSection, setActiveSection] = useState("tab1");
  const [reloadKey, setReloadKey] = useState(0); // This will force a re-render

  const handleFileUpload = useHandleFileUpload();
  const fileInputRef = React.useRef(null);

  /**
   * Resets all settings in localStorage to their initial default values.
   * Iterates over each key-value pair in initialSettings.localStorage and
   * updates localStorage accordingly. After resetting, forces a re-render
   * by incrementing the reloadKey state.
   */
  const handleResetSettings = () => {
    Object.entries(initialSettings.localStorage).forEach(([key, value]) => {
      if (key !== storageKeys.LLM_API_URL && key !== storageKeys.LLM_MODEL) {
        localStorage.setItem(key, value);
      }
    });
    // Force re-render by changing the key (triggers components to reload settings)
    setReloadKey((prevKey) => prevKey + 1);
  };

  /**
   * Exports all current localStorage settings to a JSON file.
   * Uses the downloadFile utility to trigger a download of the settings as "settings.json".
   * The exported file contains a "localStorage" object with all key-value pairs.
   */
  const handleExportSettings = () => {
    downloadFile(
      { localStorage: { ...localStorage } },
      "settings.json",
      "application/json"
    );
  };

  /**
   * Handles importing settings from a user-selected file.
   * Reads the file, parses its contents, and restores localStorage settings.
   * After importing, forces a re-render by updating reloadKey.
   * If an error occurs, alerts the user.
   * Always clears the file input value to allow re-uploading the same file.
   *
   * @param {Event} e - The file input change event.
   */
  const handleImportSettings = async (e) => {
    const file = e.target.files[0]; // Get the selected file

    if (file) {
      // Use the custom file upload handler to read and parse the file
      handleFileUpload(file)
        .then((importedData) => {
          // If the imported data contains localStorage settings, restore them
          if (importedData.localStorage) {
            Object.entries(importedData.localStorage).forEach(
              ([key, value]) => {
                localStorage.setItem(key, value);
              }
            );
          }
          // Force a re-render by incrementing the reloadKey state
          setReloadKey((prevKey) => prevKey + 1);
        })
        .catch(() => {
          alert("There was an error importing settings.");
        });
    }

    // Clear the file input value so the same file can be uploaded again if needed
    e.target.value = "";
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-xl modal-full"
      centered
    >
      <Modal.Body className="d-flex p-0">
        {/* Vertical Nav Bar */}
        <Nav
          className="flex-column py-5 px-4 nav-settings"
          style={{ width: "225px" }}
        >
          <h5 className="mb-3">Settings</h5>
          <Nav.Link
            active={activeSection === "tab1"}
            onClick={() => setActiveSection("tab1")}
            style={{ whiteSpace: "nowrap" }}
          >
            LLM Settings
          </Nav.Link>
          <Nav.Link
            active={activeSection === "tab2"}
            onClick={() => setActiveSection("tab2")}
            style={{ whiteSpace: "nowrap" }}

          >
            LLM Instructions
          </Nav.Link>
          <Nav.Link
            active={activeSection === "tab3"}
            onClick={() => setActiveSection("tab3")}
            style={{ whiteSpace: "nowrap" }}

          >
            Schema Settings
          </Nav.Link>
          <Nav.Link
            active={activeSection === "tab4"}
            onClick={() => setActiveSection("tab4")}
            style={{ whiteSpace: "nowrap" }}
          >
            Checkers Manager
          </Nav.Link>

          {/* Import & Export Buttons */}
          <div className="mt-auto pt-4">
            <Button
              variant="outline-dark"
              size="sm"
              className="w-100 mb-2"
              onClick={handleResetSettings}
            >
              Reset Settings
            </Button>
            <Button
              variant="outline-dark"
              size="sm"
              className="w-100 mb-2"
              onClick={handleExportSettings}
            >
              Export Settings
            </Button>
            <Button
              variant="outline-dark"
              size="sm"
              className="w-100"
              onClick={() => fileInputRef.current.click()}
            >
              Import Settings
            </Button>
            {/* Hidden file input */}
            <input
              type="file"
              accept="application/json"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImportSettings}
            />
          </div>
        </Nav>

        {/* Content Area */}
        <div key={reloadKey} className="bg-light flex-grow-1 py-3 pb-5 px-5">
          <div className="d-flex justify-content-end">
            <Button
              variant="transparent"
              onClick={handleClose}
              className="fs-2 fw-bolder p-0 modal-close"
            >
              ×
            </Button>
          </div>

          {activeSection === "tab1" && <LLMSettings />}

          {activeSection === "tab2" && <InstructionsSettings />}

          {activeSection === "tab3" && <SchemaSettings />}

          {activeSection === "tab4" && <CheckerManagerSettings />}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SettingsModal;
